import {useCallback, useEffect, useRef, useState} from 'react';

import {useQuery} from '@apollo/react-hooks';
import {NetworkStatus} from 'apollo-client';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetRelatedContacts as GetRelatedContactsQuery,
    GetRelatedContacts_account_contacts as Contacts,
    GetRelatedContactsVariables,
    PeepRelatedPeepSortType,
} from 'nutshell-graphql-types';

import GET_RELATED_CONTACTS from '../queries/get-related-contacts.graphql';

const INITIAL_VISIBLE_CONTACTS = 5;
const EXPANDED_VISIBLE_CONTACTS = 1000;

export function useGetRelatedContacts(
    accountId: string,
    formerOnly: boolean,
    skip: boolean = false,
    search?: string | null,
    sortBy?: PeepRelatedPeepSortType | null
): {
    contacts: Contacts | null;
    totalCount: number;
    isLoading: boolean;
    isLoadingMore: boolean;
    isRefreshing: boolean;
    isRefetching: boolean;
    isExpanded: boolean;
    hasNextPage: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchAllContacts: () => void;
} {
    const [requestedFirst, setRequestedFirst] = useState<number>(INITIAL_VISIBLE_CONTACTS);

    const prevSearchRef = useRef<string | null | undefined>(search);
    const prevSortByRef = useRef<PeepRelatedPeepSortType | null | undefined>(sortBy);
    const fetchAllRef = useRef<boolean>(false);

    const hasSearchChanged = prevSearchRef.current !== search;
    const hasSortChanged = prevSortByRef.current !== sortBy;

    if (hasSearchChanged || hasSortChanged) {
        fetchAllRef.current = false;
    }

    // Reset to collapsed state when search changes
    if (hasSearchChanged) {
        prevSearchRef.current = search;
        if (requestedFirst !== INITIAL_VISIBLE_CONTACTS) {
            setRequestedFirst(INITIAL_VISIBLE_CONTACTS);
        }
    }

    if (hasSortChanged) {
        prevSortByRef.current = sortBy;
    }

    const {data, loading, error, refetch, networkStatus} = useQuery<
        GetRelatedContactsQuery,
        GetRelatedContactsVariables
    >(GET_RELATED_CONTACTS, {
        variables: {id: accountId, formerOnly, first: requestedFirst, search, sortBy},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip,
    });

    // Reset fetchAllRef when loading is complete
    const prevLoadingRef = useRef(loading);
    useEffect(() => {
        if (prevLoadingRef.current && !loading) {
            fetchAllRef.current = false;
        }
        prevLoadingRef.current = loading;
    }, [loading]);

    const contacts = data?.account?.contacts ?? null;
    const totalCount = contacts?.totalCount ?? 0;
    const currentCount = contacts?.edges.length ?? 0;
    const hasNextPage = contacts?.pageInfo?.hasNextPage ?? false;
    const isExpanded = currentCount > INITIAL_VISIBLE_CONTACTS;

    const fetchAllContacts = useCallback(() => {
        if (totalCount > INITIAL_VISIBLE_CONTACTS) {
            fetchAllRef.current = true;
            setRequestedFirst(EXPANDED_VISIBLE_CONTACTS);
        }
    }, [totalCount]);

    // When expanded, use the full expanded page size so that newly added items are included.
    // When not expanded, use the initial page size.
    const refetchWithCurrentCount = useCallback(() => {
        const first = isExpanded ? requestedFirst : INITIAL_VISIBLE_CONTACTS;
        return refetch({id: accountId, formerOnly, first, search, sortBy});
    }, [accountId, formerOnly, isExpanded, requestedFirst, search, sortBy, refetch]);

    return {
        contacts,
        totalCount,
        isLoading: loading && !contacts,
        isLoadingMore: loading && fetchAllRef.current,
        isRefreshing: loading && Boolean(contacts) && !fetchAllRef.current,
        isRefetching: networkStatus === NetworkStatus.refetch,
        isExpanded,
        hasNextPage,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch: refetchWithCurrentCount,
        fetchAllContacts,
    };
}
